'use client';

import { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import {
  BarChart3,
  ClipboardList,
  FileText,
  LayoutDashboard,
  BadgePercent,
  Package,
  Settings,
  ShoppingCart,
  Truck,
  Boxes,
  Users,
  ShieldCheck,
  ScrollText,
  Building2,
  UserCircle,
  Store,
  Tags,
  FolderTree,
  Tag,
  Sparkles,
} from "lucide-react";
import { MainLayout } from "../components/layout";
import type { SidebarItem } from "../components/layout/types";
import { usePageTracking } from "../hooks/usePageTracking";
import { useAuth } from "../providers/AuthProvider";
import { POS_V2_ENABLED } from "../features/pos-v2/flags";

/** Router-level `RouteAnalyticsLayout` also calls `usePageTracking`; shared dedupe skips duplicate `page_view`. */
export function AppShell() {
  usePageTracking();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const sidebarItems: SidebarItem[] = useMemo(
    () =>
      isSuperAdmin
        ? [
            { icon: LayoutDashboard, label: "Dashboard", href: "/admin" },
            { icon: Users, label: "Users", href: "/admin/users" },
            { icon: Building2, label: "Businesses", href: "/admin/businesses" },
            { icon: BarChart3, label: "Reports", href: "/admin/reports" },
            { icon: ShieldCheck, label: "Verifications", href: "/admin/verifications" },
            { icon: ScrollText, label: "Audit log", href: "/admin/audit" },
            { icon: UserCircle, label: "Profile", href: "/admin/profile" },
            {
              icon: Tags,
              label: "Catalog management",
              children: [
                { icon: FolderTree, label: "Manage Categories", href: "/settings/categories" },
                { icon: Tag, label: "Manage Brands", href: "/settings/brands" },
              ],
            },
            {
              icon: BadgePercent,
              label: "Offer management",
              children: [
                { icon: BadgePercent, label: "Offers", href: "/settings/promotions" },
                { icon: Settings, label: "Stacking", href: "/settings/promotions/stacking" },
              ],
            },
          ]
        : [
            { icon: LayoutDashboard, label: "Dashboard", href: "/" },
            { icon: ShoppingCart, label: "POS", href: "/pos" },
            ...(POS_V2_ENABLED ? [{ icon: Sparkles, label: "POS (classic)", href: "/pos-v1" }] : []),
            { icon: ScrollText, label: "Sales", href: "/sales" },
            {
              icon: Tags,
              label: "Catalog management",
              children: [
                { icon: FolderTree, label: "Manage Categories", href: "/settings/categories" },
                { icon: Tag, label: "Manage Brands", href: "/settings/brands" },
              ],
            },
            {
              icon: BadgePercent,
              label: "Offer management",
              children: [
                { icon: BadgePercent, label: "Offers", href: "/settings/promotions" },
                { icon: Settings, label: "Stacking", href: "/settings/promotions/stacking" },
              ],
            },
            { icon: Package, label: "Inventory", href: "/inventory" },
            { icon: Boxes, label: "Products", href: "/products" },
            {
              icon: ClipboardList,
              label: "Purchase Orders",
              href: "/purchase-orders",
            },
            { icon: Truck, label: "Dealers", href: "/dealers" },
            { icon: FileText, label: "Reports", href: "/reports" },
            { icon: BarChart3, label: "Analytics", href: "/analytics" },
            { icon: Store, label: "Business profile", href: "/settings/business-profile" },
            { icon: Settings, label: "Settings", href: "/settings" },
          ],
    [isSuperAdmin, POS_V2_ENABLED],
  );

  return (
    <MainLayout
      sidebarItems={sidebarItems}
      activePath={location.pathname}
      isSidebarCollapsed={isSidebarCollapsed}
      onSidebarToggle={() => setIsSidebarCollapsed((v) => !v)}
      sidebarRenderItem={(item, itemClassName, content, _isActive) => (
        <NavLink
          to={item.href}
          end={item.href === "/" || item.href === "/admin"}
          className={itemClassName}
        >
          {content}
        </NavLink>
      )}
    >
      <Outlet />
    </MainLayout>
  );
}

